import { Component, Input, Output, EventEmitter, HostListener, OnChanges, SimpleChanges, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin, Subscription } from 'rxjs';
import { DeliveryService, InvoiceDetails, InvoiceLine, InvoicePaymentItem } from '../../services/delivery.service';
import { ReturnComponent, ReturnItem } from '../return-component/return-component';
import { Customer, CustomerService } from '../../services/customer';
import { MatButtonModule } from '@angular/material/button';
import { CollectPaymentDialogComponent } from '../collect-payment-dialog-component/collect-payment-dialog-component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-invoice-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    ReturnComponent
  ],
  templateUrl: './invoice-detail-component.html',
  styleUrls: ['./invoice-detail-component.scss'],
})
export class InvoiceDetailComponent implements OnChanges, OnDestroy {
  private readonly returnHistoryStateKey = 'a1-route-sales-invoice-return';
  private readonly detailsHistoryStateKey = 'a1-route-sales-invoice-details';

  /** Either provide invoiceId to fetch, or pass a ready invoice */
  @Input() invoiceId?: number | null;
  @Input() invoice?: InvoiceDetails | null;
  selectedCustomer: Customer | null = null;
  returnItems: ReturnItem[] = [];
  showReturn = false;

  /** Optional toggles */
  @Input() showPayments = true;
  @Input() showCustomer = true;
  @Input() currencyFallback = 'INR';
  @Input() loadId?: number;

  /** Parent can react when loaded / when user wants to collect payment */
  @Output() invoiceLoaded = new EventEmitter<InvoiceDetails>();
  @Output() collectPayment = new EventEmitter<InvoiceDetails>();
  @Output() closedDetails = new EventEmitter();

  loading = signal<boolean>(false);
  error = signal<string | null>(null);
  invSig = signal<InvoiceDetails | null>(null);

  currency = computed(() => this.invSig()?.currency ?? this.currencyFallback);
  hasLines = computed(() => (this.invSig()?.lines?.length ?? 0) > 0);
  hasPayments = computed(() => (this.invSig()?.payments?.length ?? 0) > 0);
  customerHasPendingInvoices = signal<boolean>(false);
  pendingInvoicesLoading = signal<boolean>(false);

  private sub?: Subscription;

  constructor(
    private api: DeliveryService,
    private dialog: MatDialog,
    private customerService: CustomerService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    // If parent passes an invoice object, prefer it
    if ('invoice' in changes && this.invoice) {
      this.setInvoice(this.invoice);
      return;
    }
    // Otherwise fetch by ID when it changes
    if ('invoiceId' in changes && this.invoiceId) {
      this.fetch(this.invoiceId);
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private setInvoice(inv: InvoiceDetails) {
    this.error.set(null);
    this.loading.set(false);

    if (!inv.partnerId) {
      this.finalizeInvoice(inv);
      return;
    }

    // Combine customer and credit note requests in parallel
    forkJoin({
      customers: this.customerService.get(),
      creditsResponse: this.api.getOpenCreditsForCustomer(inv.partnerId)
    }).subscribe({
      next: ({ customers, creditsResponse }) => {
        const customer = customers.find(c => c.id === inv.partnerId);
        if (customer) {
          this.selectedCustomer = customer;
        } else {
          this.error.set(`Customer not found with ID: ${inv.partnerId}`);
        }

        if (creditsResponse?.status === 'success' && creditsResponse.credits) {
          inv.totals.totalCredits = creditsResponse.credits.reduce(
            (sum, credit) => sum + credit.openAmount,
            0
          );
        }

        this.finalizeInvoice(inv);
      },
      error: (err) => {
        console.warn('Failed to load customer or credit notes:', err);
        this.error.set('Failed to load customer or credit notes.');
        this.finalizeInvoice(inv); // Still proceed
      }
    });
  }

  private finalizeInvoice(inv: InvoiceDetails) {
    this.invSig.set(inv);
    this.invoiceLoaded.emit(inv);
    this.refreshCustomerPendingInvoices(inv);
  }


  private fetch(id: number) {
    this.sub?.unsubscribe();
    this.loading.set(true);
    this.error.set(null);
    this.invSig.set(null);
    this.sub = this.api.getInvoice(id).subscribe({
      next: inv => this.setInvoice(inv),
      error: err => {
        this.loading.set(false);
        this.error.set(err?.message ?? 'Failed to load invoice');
      }
    });
  }

  // template helpers
  trackLine(_i: number, l: InvoiceLine) { return l.id; }
  trackPay(_i: number, p: InvoicePaymentItem) { return p.paymentId ?? p.moveName ?? _i; }

  onCollectClick() {
    const inv = this.invSig();
    if (!inv?.partnerId) return;

    this.openCollectPaymentDialog(inv.partnerId);
    this.collectPayment.emit(inv);
  }

  onReturnItemsChange(items: ReturnItem[]) {
    // live changes (e.g., enable/disable a save button)
  }

  onReturnCreated() {
    if(this.invoiceId)
      this.fetch(this.invoiceId);
    this.returnItems = [];
    this.showReturn = false;

    if (this.isCurrentReturnHistoryState()) {
      window.history.replaceState({ screen: this.detailsHistoryStateKey }, '', window.location.href);
    }
  }

  onShowReturn(): void {
    this.showReturn = true;
    this.pushReturnHistoryState();
  }

  closeDetails(syncBrowserHistory: boolean = true) {
    if (this.showReturn) {
      if (syncBrowserHistory && this.isCurrentReturnHistoryState()) {
        window.history.back();
        return;
      }

      this.showReturn = false;
    }
    else
      this.closedDetails.emit();
  }

  @HostListener('window:popstate', ['$event'])
  onBrowserBack(event: PopStateEvent): void {
    if (!this.showReturn) return;

    // Ignore internal history movements that keep us on the Return screen
    // (for example product-search dropdown close after selecting an item).
    if (event.state?.screen === this.returnHistoryStateKey) return;

    this.closeDetails(false);
  }

  private pushReturnHistoryState(): void {
    if (this.isCurrentReturnHistoryState()) return;
    window.history.pushState({ screen: this.returnHistoryStateKey }, '', window.location.href);
  }

  private isCurrentReturnHistoryState(): boolean {
    return window.history.state?.screen === this.returnHistoryStateKey;
  }

  openCollectPaymentDialog(customerId: number) {
    const dialogRef = this.dialog.open(CollectPaymentDialogComponent, {
      width: '400px',
      data: { customerId, invoiceId: this.invoiceId, loadId: this.loadId}
    });

    dialogRef.afterClosed().subscribe(paymentResponse => {
      if (paymentResponse) {
        console.log('Payment collected:', paymentResponse);
        // Refresh the invoice data to show updated balance
        if (this.invoiceId) {
          this.fetch(this.invoiceId);
        }
      }
    });
  }

  hasCoords(): boolean {
    const c = this.selectedCustomer;
    if (!c) return false;
    const lat = Number(c.latitude);
    const lng = Number(c.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
  }

  isCollectPaymentDisabled(): boolean {
    const inv = this.invSig();
    if (!inv?.partnerId) return true;

    const currentInvoiceClosed = inv.paymentState === 'paid' || inv.paymentState === 'reversed';
    if (!currentInvoiceClosed) return false;

    if (this.pendingInvoicesLoading()) return true;
    return !this.customerHasPendingInvoices();
  }

  isReturnDisabled(): boolean {
    const inv = this.invSig();
    return inv?.paymentState === 'paid' || inv?.paymentState === 'reversed';
  }

  private refreshCustomerPendingInvoices(inv: InvoiceDetails): void {
    if (!inv.partnerId) {
      this.customerHasPendingInvoices.set(false);
      this.pendingInvoicesLoading.set(false);
      return;
    }

    this.pendingInvoicesLoading.set(true);
    this.api.getPendingInvoicesForCustomer(inv.partnerId, { limit: 1 }).subscribe({
      next: (response) => {
        const hasPending = response.status === 'success' && ((response.total || 0) > 0 || (response.invoices?.length || 0) > 0);
        this.customerHasPendingInvoices.set(!!hasPending);
        this.pendingInvoicesLoading.set(false);
      },
      error: () => {
        this.customerHasPendingInvoices.set(false);
        this.pendingInvoicesLoading.set(false);
      }
    });
  }
  
  openMap(): void {
    const c = this.selectedCustomer;
    if (!c || !this.hasCoords()) {
      alert('No latitude/longitude found for this customer.');
      return;
    }
  
    const lat = Number(c.latitude);
    const lng = Number(c.longitude);
    const label = encodeURIComponent(c.name || 'Customer');
  
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  
    if (isMobile) {
      // Prefer native map apps on mobile
      // Android/iOS typically handle geo: URIs
      const geoUri = `geo:${lat},${lng}?q=${lat},${lng}(${label})`;
      window.location.href = geoUri;
    } else {
      // Desktop fallback: open Google Maps in a new tab
      const web = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      window.open(web, '_blank');
    }
  }
}
